import { useEffect } from 'react';
import { scheduleHubspotTicketRowScroll } from '../../lib/hubspot-management/scroll';
import {
  type HubspotTicketDetail,
  selectTicket,
  setPage,
  setPendingSelection,
} from '../../stores/hubspotManagementStore';

interface UseHubspotTicketNavigationOptions {
  tickets: HubspotTicketDetail[];
  selectedTicketId: string | null;
  pendingTicketSelection: 'first' | 'last' | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
}

export function useHubspotTicketNavigation({
  tickets,
  selectedTicketId,
  pendingTicketSelection,
  pagination,
}: UseHubspotTicketNavigationOptions) {
  useEffect(() => {
    if (pendingTicketSelection && tickets.length > 0 && selectedTicketId) {
      const currentTicketExists = tickets.some(
        (ticket) => ticket.id === selectedTicketId,
      );

      if (!currentTicketExists) {
        const ticketToSelect =
          pendingTicketSelection === 'first'
            ? tickets[0].id
            : tickets[tickets.length - 1].id;

        selectTicket(ticketToSelect);
        setPendingSelection(null);
        scheduleHubspotTicketRowScroll(ticketToSelect, 100);
      }
    }
  }, [tickets, pendingTicketSelection, selectedTicketId]);

  const handleNavigate = (direction: 'prev' | 'next') => {
    const currentIdx = tickets.findIndex(
      (ticket) => ticket.id === selectedTicketId,
    );
    let newTicketId: string | null = null;

    if (direction === 'next') {
      if (currentIdx < tickets.length - 1) {
        newTicketId = tickets[currentIdx + 1].id;
      } else if (pagination.currentPage < pagination.totalPages) {
        setPendingSelection('first');
        setPage(pagination.currentPage + 1);
        return;
      }
    } else {
      if (currentIdx > 0) {
        newTicketId = tickets[currentIdx - 1].id;
      } else if (pagination.currentPage > 1) {
        setPendingSelection('last');
        setPage(pagination.currentPage - 1);
        return;
      }
    }

    if (newTicketId) {
      selectTicket(newTicketId);
      scheduleHubspotTicketRowScroll(newTicketId, 0);
    }
  };

  return {
    handleNavigate,
  };
}
